const MINOR_WORDS = new Set([
	"a",
	"an",
	"and",
	"as",
	"at",
	"but",
	"by",
	"for",
	"in",
	"nor",
	"of",
	"on",
	"or",
	"so",
	"the",
	"to",
	"up",
	"yet",
	"with",
]);

// Event names come straight from organizers typing into the CMS, so
// casing is inconsistent (ALL CAPS, Title Case, etc). Normalize for
// display without mangling real acronyms (GCAS, BBQ) or compound
// tokens like "1st" / "BBQ/Picnic" — only touch words that are purely
// alphabetic and either fully upper or fully lower to begin with.
export function toTitleCase(name) {
	const words = name.trim().split(/\s+/);
	return words
		.map((word, i) => {
			const isPureAlpha = /^[A-Za-z]+$/.test(word);
			if (!isPureAlpha) return word;

			const lower = word.toLowerCase();
			if (i !== 0 && i !== words.length - 1 && MINOR_WORDS.has(lower)) {
				return lower;
			}

			const isAllUpper = word === word.toUpperCase();
			const isAllLower = word === lower;
			if (isAllUpper && word.length <= 4) return word;
			if (isAllUpper || isAllLower) {
				return word[0].toUpperCase() + lower.slice(1);
			}
			return word;
		})
		.join(" ");
}

export function isPastEvent(dateString) {
	return new Date(dateString).getTime() < Date.now();
}

export function formatEventDate(dateString) {
	const date = new Date(dateString);
	const now = new Date();
	const options = { month: "short", day: "numeric" };
	if (date.getFullYear() !== now.getFullYear()) {
		options.year = "numeric";
	}
	return date.toLocaleDateString("en-US", options);
}

// Powers the event detail page's ticket-stub date block, which needs
// the month/day/year/weekday as separate pieces rather than one string.
export function getDateParts(dateString) {
	const date = new Date(dateString);
	return {
		month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
		day: date.toLocaleDateString("en-US", { day: "numeric" }),
		year: date.toLocaleDateString("en-US", { year: "numeric" }),
		weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
	};
}

export function formatLongDate(dateString) {
	return new Date(dateString).toLocaleDateString("en-US", {
		weekday: "long",
		month: "long",
		day: "numeric",
		year: "numeric",
	});
}

export function formatTime(dateString) {
	return new Date(dateString).toLocaleTimeString("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
}

// The CMS's eventType options are "In-person" / "Virtual" / "Hybrid", but
// real entries drift in casing ("in-person", "VIRTUAL"). Normalize to the
// canonical form rather than trusting the stored casing verbatim.
export function formatEventType(eventType) {
	if (!eventType) return null;
	return eventType.charAt(0).toUpperCase() + eventType.slice(1).toLowerCase();
}

// One-line ticket summary for the detail card's compact fact row —
// the full per-tier breakdown isn't worth a whole row when real data
// is almost always a single untyped tier.
export function getTicketSummary(tickets) {
	if (!tickets?.length) return null;
	const available = tickets.filter((t) => t.availability !== "sold_out");
	if (available.length === 0) return "Sold out";
	const prices = available.map((t) => t.price || 0).filter((p) => p > 0);
	if (prices.length === 0) return "Free";
	const min = Math.min(...prices);
	const max = Math.max(...prices);
	return min === max ? `$${min}` : `From $${min}`;
}

// The venue schema models address as a structured object, but real
// documents were written before that change and store a plain string.
// Handle both shapes rather than assuming one.
export function formatAddress(address) {
	if (!address) return null;
	if (typeof address === "string") return address;
	return [address.street, address.city, address.state, address.zipCode]
		.filter(Boolean)
		.join(", ");
}

// Most real venues only ever get a plain address typed in, not a saved
// Google Maps link — fall back to a maps search built from that address
// (or the venue name) rather than leaving the action unavailable.
export function getMapUrl(venue) {
	if (!venue) return null;
	if (venue.googleMapsLocation) return venue.googleMapsLocation;
	const query = formatAddress(venue.address) || venue.name;
	if (!query) return null;
	return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function escapeIcsText(text) {
	return String(text)
		.replace(/\\/g, "\\\\")
		.replace(/,/g, "\\,")
		.replace(/;/g, "\\;")
		.replace(/\n/g, "\\n");
}

function toIcsDate(date) {
	return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

// A standards-based .ics download works across Google/Apple/Outlook
// without picking one provider's own add-to-calendar link format.
export function buildCalendarUrl(event) {
	if (!event?.date) return null;

	const start = new Date(event.date);
	const end = event.endDate
		? new Date(event.endDate)
		: new Date(start.getTime() + 2 * 60 * 60 * 1000);

	const location = event.venue?.name
		? [event.venue.name, formatAddress(event.venue.address)]
				.filter(Boolean)
				.join(", ")
		: formatEventType(event.eventType) || "";

	const lines = [
		"BEGIN:VCALENDAR",
		"VERSION:2.0",
		"PRODID:-//GCAS Regina//Events//EN",
		"BEGIN:VEVENT",
		`UID:${event._id}@gcasregina.ca`,
		`DTSTAMP:${toIcsDate(new Date())}`,
		`DTSTART:${toIcsDate(start)}`,
		`DTEND:${toIcsDate(end)}`,
		`SUMMARY:${escapeIcsText(toTitleCase(event.name))}`,
		location && `LOCATION:${escapeIcsText(location)}`,
		"END:VEVENT",
		"END:VCALENDAR",
	].filter(Boolean);

	return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join("\r\n"))}`;
}
